import { defineComponent as _defineComponent } from 'vue'
import { offset, flip } from '@floating-ui/vue'
import { storeToRefs } from 'pinia'
import { onMounted, ref, toRef, watch } from 'vue'

import RouteItem from './RouteItem.vue'
import LButton from '@/components/core/Button.vue'
import { useContext } from '@/composables/context'
import { usePopover, useDismiss } from '@/composables/popover'
import { useRouteManagement } from '@/composables/route'
import { useSelectedTrip } from '@/composables/trip'
import { useTransportationStore } from '@/stores/transportation'
import { RouteAvoidOption, TransportationUIInput } from '@/types/transportation'


export default /*#__PURE__*/_defineComponent({
  __name: 'RouteManager',
  props: {
    transportation: null
  },
  setup(__props: any) {

const props = __props as {
  transportation: TransportationUIInput
};


const transportation = toRef(props, 'transportation')

const { i18n } = useContext()
const trip = useSelectedTrip()
const transportationStore = useTransportationStore()
const { availableRoutes, showAlternativeRoutes } =
  storeToRefs(transportationStore)

const isExpanded = ref(false)

// Avoid options (driving only) ------------------------------------------------
const avoidOptions = ref<RouteAvoidOption[]>(
  transportation.value.route?.avoidOptions ?? [],
)

const avoidOptionsList = [
  { key: 'tolls', label: i18n.t('transportation.route.tolls').toString() },
  {
    key: 'highways',
    label: i18n.t('transportation.route.highways').toString(),
  },
  {
    key: 'ferries',
    label: i18n.t('transportation.route.ferries').toString(),
  },
] satisfies { key: RouteAvoidOption; label: string }[]

// Avoid options popover -------------------------------------------------------
const isAvoidOpen = ref(false)
const {
  referenceRef: avoidButtonRef,
  floatingRef: avoidPanelRef,
  floatingStyles: avoidFloatingStyles,
} = usePopover({
  placement: 'bottom-end',
  isOpen: isAvoidOpen,
  middleware: [offset(4), flip({ padding: 16 })],
})
useDismiss({
  referenceRef: avoidButtonRef,
  floatingRef: avoidPanelRef,
  isOpen: isAvoidOpen,
})

// Route management ------------------------------------------------------------
const { calculateRoutes, isLoading } = useRouteManagement(transportation)

async function calculateAlternatives() {
  if (!trip.value.activeProSubscription) {
    window.open(`${process.env.DASHBOARD_HOME}/pro/features`, '_blank')
    return
  }

  if (!isExpanded.value) {
    isExpanded.value = true
    showAlternativeRoutes.value = true
    await calculateRoutes({
      includeAlternatives: showAlternativeRoutes.value,
      avoidOptions: avoidOptions.value,
    })
  } else {
    isExpanded.value = false
    showAlternativeRoutes.value = false
  }
}

watch(
  [() => transportation.value.origin, () => transportation.value.destination],
  async () => {
    await calculateRoutes({
      includeAlternatives: showAlternativeRoutes.value,
      avoidOptions: avoidOptions.value,
    })
  },
)

watch(avoidOptions, async () => {
  if (!trip.value.activeProSubscription) {
    return
  }
  await calculateRoutes({
    includeAlternatives: showAlternativeRoutes.value,
    avoidOptions: avoidOptions.value,
    resetSelectedRoute: true,
  })
})

onMounted(async () => {
  if (!transportation.value.route) {
    await calculateRoutes({
      includeAlternatives: showAlternativeRoutes.value,
      avoidOptions: avoidOptions.value,
    })
  }
})

return { __sfc: true,props, transportation, i18n, trip, transportationStore, availableRoutes, showAlternativeRoutes, isExpanded, avoidOptions, avoidOptionsList, isAvoidOpen, avoidButtonRef, avoidPanelRef, avoidFloatingStyles, calculateRoutes, isLoading, calculateAlternatives, RouteItem, LButton }
}

})